<script setup lang="ts">
// Risk tab -> "Rules": the pot de-risking rules (spec docs/superpowers/specs/2026-11-01-pot-risk-rules-design.md),
// GET /api/risk/rules. Advisory only: the page says what the rule advises and whether the table was changed after
// it tripped; weights only ever change on the tiers screen. Two rules: the live pot's drawdown since the configured
// start, and each fund's live strategy vs its backtest expectation (the Live vs backtest "below expectation" test).
import { ref, computed, onMounted } from 'vue'
import { fundDisplayLabel } from './pricingSourceHelpers'
import { api, type RiskRulesResponse, type RiskRuleEpisode, type FundRuleStatus } from '../api/client'
import { ordinal } from './potParityFormat'
import RiskLineChart, { type RiskChartSeries } from './RiskLineChart.vue'

const data = ref<RiskRulesResponse | null>(null)
const loading = ref(true)
const error = ref('')

onMounted(async () => {
  try {
    data.value = await api.riskRules()
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e)
  } finally {
    loading.value = false
  }
})

const POT_STATE: Record<string, string> = { no_data: 'No live sessions yet', ok: 'Within limits', amber: 'Amber', red: 'Red' }
const FUND_STATE: Record<string, string> = { too_short: 'Too few sessions', ok: 'On expectation', watch: 'Watch', tripped: 'Tripped' }
const stateClass = (s: string) => (s === 'ok' ? 'v-ok' : s === 'amber' || s === 'watch' ? 'v-warn' : s === 'red' || s === 'tripped' ? 'v-bad' : 'v-grey')

const dash = '—'
const pct1 = (v: number | null | undefined) => (v == null ? dash : `${v.toFixed(1)}%`)
const pctile = (v: number | null | undefined) => (v == null ? dash : ordinal(Math.round(v)))

const pot = computed(() => data.value?.pot ?? null)
const levels = computed(() => data.value?.drawdownLevels ?? [])
const levelText = computed(() => levels.value.map(l => `${l.atPct}% ${l.level}`).join(' · '))

// Underwater chart: drawdown as a negative % with each level drawn as a flat dashed line.
const LEVEL_COLOR: Record<string, string> = { amber: '#ffb74d', red: '#ef5350' }
const chartSeries = computed<RiskChartSeries[]>(() => {
  const s = pot.value?.series ?? []
  if (s.length < 2) return []
  const out: RiskChartSeries[] = [
    { id: 'dd', label: 'Drawdown', color: '#42a5f5', lineWidth: 2, area: true, data: s.map(p => ({ time: p.date, value: -p.value })) },
  ]
  for (const l of levels.value)
    out.push({ id: `lvl-${l.atPct}`, label: `${l.atPct}%`, color: LEVEL_COLOR[l.level] ?? '#999', lineWidth: 1, dashed: true,
      data: s.map(p => ({ time: p.date, value: -l.atPct })) })
  return out
})

function episodeText(e: RiskRuleEpisode, fund: boolean): string {
  const span = `${e.start} → ${e.end ?? 'open'}`
  const worst = fund ? `worst ${pctile(e.worst)} percentile` : `worst ${pct1(e.worst)}`
  const level = fund ? (e.worstLevel === 'tripped' ? `tripped ${e.trippedOn}` : 'watch only') : e.worstLevel
  const acted = e.trippedOn == null ? '' : e.actedOn ? ` · acted ${e.actedOn}` : ' · no table change'
  return `${span} · ${level} · ${worst}${acted}`
}

const fundRows = computed<FundRuleStatus[]>(() => data.value?.funds ?? [])
const fundsWithHistory = computed(() => fundRows.value.filter(f => f.episodes.length > 0))
const fundProgress = (f: FundRuleStatus) =>
  f.state === 'too_short' ? `${f.sessions} of 20 sessions` : `${f.consecutiveBelow} of ${data.value?.fundBelowSessions ?? 0} below`
</script>

<template>
  <div class="rules" data-testid="risk-rules-view">
    <p class="rules-note">Advisory: these rules tell you when to cut risk and record whether the table changed afterwards. Weights only change on the tiers screen.</p>
    <p v-if="loading" class="rules-muted">Loading rules...</p>
    <p v-else-if="error" class="rules-error" data-testid="rules-error">Could not load the rules: {{ error }}</p>

    <template v-else-if="data && pot">
      <section class="rules-card" data-testid="rules-pot">
        <h3 class="rules-title">Pot drawdown <span class="rules-sub">since {{ pot.from ?? data.since ?? dash }} · levels {{ levelText }}</span></h3>
        <p class="rules-state" :class="stateClass(pot.state)" data-testid="rules-pot-state">
          {{ POT_STATE[pot.state] ?? pot.state }}
          <span v-if="pot.drawdownPct != null" class="rules-value">{{ pct1(pot.drawdownPct) }} below the {{ pot.peakDate }} peak</span>
        </p>
        <p v-if="pot.action" class="rules-action" data-testid="rules-pot-action">{{ pot.action }}</p>
        <div v-if="chartSeries.length" class="rules-chart" data-testid="rules-pot-chart">
          <RiskLineChart :series="chartSeries" format="pct1" :height="180" />
        </div>
        <ul v-if="pot.episodes.length" class="rules-episodes">
          <li v-for="(e, i) in pot.episodes" :key="i" data-testid="rules-pot-episode">{{ episodeText(e, false) }}</li>
        </ul>
      </section>

      <section class="rules-card" data-testid="rules-funds">
        <h3 class="rules-title">Funds vs backtest <span class="rules-sub">trips after {{ data.fundBelowSessions }} sessions in a row below the 5th percentile of the backtest</span></h3>
        <p v-if="!fundRows.length" class="rules-muted">No active funds to check.</p>
        <table v-else class="rules-table">
          <thead><tr><th>Fund</th><th>State</th><th>Run</th><th>CAGR pct</th><th>Sharpe pct</th></tr></thead>
          <tbody>
            <tr v-for="f in fundRows" :key="f.fundId" :data-testid="`rules-fund-${f.fundId}`">
              <td>{{ fundDisplayLabel(f.fundId) }}</td>
              <td :class="stateClass(f.state)">{{ FUND_STATE[f.state] ?? f.state }}</td>
              <td>{{ fundProgress(f) }}</td>
              <td>{{ pctile(f.cagrPctile20) }}</td>
              <td>{{ pctile(f.sharpePctile20) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-for="f in fundRows.filter(x => x.action)" :key="`a-${f.fundId}`" class="rules-action" :data-testid="`rules-fund-action-${f.fundId}`">
          {{ fundDisplayLabel(f.fundId) }}: {{ f.action }}
        </p>
        <ul v-if="fundsWithHistory.length" class="rules-episodes">
          <template v-for="f in fundsWithHistory" :key="`h-${f.fundId}`">
            <li v-for="(e, i) in f.episodes" :key="i" data-testid="rules-fund-episode">{{ fundDisplayLabel(f.fundId) }}: {{ episodeText(e, true) }}</li>
          </template>
        </ul>
      </section>
    </template>
  </div>
</template>

<style scoped>
.rules { display: flex; flex-direction: column; gap: 0.75rem; }
.rules-note { color: #999; font-size: 0.8rem; margin: 0; }
.rules-muted { color: #888; }
.rules-error { color: #ef5350; }
.rules-card { background: #1a1a1a; border: 1px solid #333; border-radius: 6px; padding: 0.75rem; display: flex; flex-direction: column; gap: 0.5rem; min-width: 0; }
.rules-title { margin: 0; font-size: 0.95rem; color: #ddd; }
.rules-sub { display: block; color: #888; font-size: 0.75rem; font-weight: 400; margin-top: 0.15rem; }
.rules-state { margin: 0; font-size: 1rem; font-weight: 600; }
.rules-value { margin-left: 0.4rem; color: #ccc; font-weight: 400; font-size: 0.85rem; }
.rules-action { margin: 0; padding: 0.4rem 0.6rem; border-left: 3px solid #ffb74d; background: #222; color: #eee; font-size: 0.85rem; }
.rules-chart { min-width: 0; }
.rules-episodes { list-style: none; margin: 0; padding: 0; font-size: 0.78rem; color: #bbb; display: flex; flex-direction: column; gap: 0.2rem; overflow-wrap: anywhere; }
.rules-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; color: #ccc; }
.rules-table th { text-align: left; color: #888; font-weight: 400; padding: 0.25rem 0.3rem; border-bottom: 1px solid #333; }
.rules-table td { padding: 0.3rem; border-bottom: 1px solid #262626; }
.v-ok { color: #4caf50; }
.v-warn { color: #ffb74d; }
.v-bad { color: #ef5350; }
.v-grey { color: #999; }
</style>
